import classnames from "classnames";
import {
  AnalyticsComponent,
  SolutionCardModal,
  WarningModal,
} from "components";
import { MapIcon, ViewListIcon } from "@heroicons/react/outline";
import { useMemo, useState } from "react";
import { ReactComponent as SquareLeft } from "assets/images/square-left.svg";
import { ReactComponent as SquareRight } from "assets/images/square-right.svg";

import { useOutletContext } from "react-router-dom";
import { getRating } from "utils";

import { SolutionCard } from "./SolutionCard";

import { ListMap } from "components";
import { useSolutionListFilterParams } from "./SolutionList.hooks";
import { useResizeDetector } from "react-resize-detector";

import { useWindowDimensions } from "hooks";
import JumboImage1 from "assets/images/jumbo-1.png";
import { IOutletAuth } from "types";

function SolutionList() {
  const authContext = useOutletContext<IOutletAuth>();

  const [open, setOpen] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [openedCardId, setOpenedCardId] = useState<string | undefined>(
    undefined
  );
  const { width, height, ref: mapRef } = useResizeDetector();
  const [isIssueHovered, setIsIssueHovered] = useState(false);
  const dim = useWindowDimensions();

  const disabled = useMemo(() => {
    return !!(open && dim.width && dim.width <= 1023);
  }, [dim.width, open]);

  const [hoveredIssueId, setHoveredIssueId] = useState<
    string | undefined | string[]
  >(undefined);
  const [hoveredIssueType, setHoveredIssueType] = useState<
    "regular" | "city" | "country" | undefined
  >();

  const [issueHoveredProps, setIssueHoveredProps] = useState({
    lat: NaN,
    lng: NaN,
  });

  const { data, inViewRef } = useSolutionListFilterParams(
    width || dim.width,
    disabled
  );

  const [openWarningModal, setOpenWarningModal] = useState(false);
  let noSolutionsFound = true;
  if (data?.pages?.length) {
    noSolutionsFound = !data.pages.some(
      (page) => page?.data?.items && page.data.items.length > 0
    );
  }
  const handleProposeSolution = () => {
    setOpenWarningModal(true);
  };

  return (
    <div className={classnames("flex flex-col relative h-full pb-32 md:pb-20 lg:pb-12")}>
      <div
        className={classnames(
          "grid lg:grid-cols-[1fr_1fr_1fr_1.3fr]   h-full lg:h-[calc(100vh-118px)]  lg:mr-0 gap-4    "
        )}
      >
        <div
          className={classnames(
            " four-fourth-step five-fourth-step six-third-step seven-fourth-step grid grid-cols-1 auto-rows-max gap-4 lg:overflow-scroll pb-4 pt-[3px] pl-[3px]    pr-2 max-w-[2000px] transition-all duration-500 ease-linear",
            {
              "sm:grid-cols-2 lg:grid-cols-3 lg:col-span-3": !open,
              "hidden lg:grid max-w-[500px]": open,
            }
          )}
        >
          <div
            className={classnames(
              "  relative   flex-1 max-h-[300px]  w-full  overflow-hidden",
              {
                " flex  col-span-1 sm:col-span-2  lg:col-span-3": !open,
              },
              {
                hidden: open,
              }
            )}
          >
            <div className=" flex justify-center flex-1 w-full h-full">
              <img
                style={{ flexBasis: "100%" }}
                className="select-none pointer-events-none min-h-[120px] sm:min-h-[180px] flex-1 w-full h-full object-contain"
                alt="jumbo"
                src={authContext.backgroundImages || JumboImage1}
              />
            </div>
          </div>
          {data?.pages.map((page, pagesIndex) => {
            return page.data.items.map((solution, solutionIndex) => {
                const displayLabel = solution?.issue.location?.displayLabel;
                const isFormattedAddress = !!(
                  displayLabel || solution?.issue.location?.formattedAddress
                );
              const isCountry = !!solution?.issue?.location?.country;
              const isCity = !!solution?.issue?.location?.city;
              const isStreetNumber = !!solution?.issue?.location?.streetNumber;
              const isRoute = !!solution?.issue?.location?.route;
              const location =
                typeof solution?.issue?.location === "object"
                  ? isFormattedAddress
                    ? `${displayLabel || solution?.issue?.location?.formattedAddress}`
                    : isCountry && isCity && isStreetNumber && isRoute
                    ? `${solution?.issue?.location?.route} ${solution?.issue?.location?.streetNumber}, ${solution?.issue?.location?.city}, ${solution?.issue?.location?.country}`
                    : isCountry && isCity
                    ? ` ${solution?.issue?.location?.city}, ${solution?.issue?.location?.country}`
                    : isCountry
                    ? `${solution?.issue?.location?.country}`
                    : "No location"
                  : "No location";
              const geoLocation = {
                lat: solution?.issue.location.lat,
                lng: solution?.issue.location.lng,
              };
              const isLast =
                data?.pages.length === pagesIndex + 1 &&
                page.data.items.length === solutionIndex + 1;

              const issueType =
                solution?.issue?.location?.administrativeArea ||
                solution?.issue?.location?.route ||
                solution?.issue?.location?.streetNumber
                  ? "regular"
                  : solution?.issue?.location?.city
                  ? "city"
                  : "country";

              return (
                <div
                  className={classnames(
                    `relative solution-card cursor-pointer z-[${
                      solutionIndex + 10
                    }]  maxmd:flex`,
                    {
                      " first-card-to-click ": solutionIndex === 0,
                    }
                  )}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    solution && setOpenedCardId(solution.id);
                    solution && setOpenCardModal(true);
                  }}
                  key={solution.id}
                >
                  {solution.analyticsData && (
                    <AnalyticsComponent
                      analyticsData={solution.analyticsData}
                    />
                  )}
                  <SolutionCard
                    solutionId={solution.id}
                    id={solution?.user.id}
                    skills={solution?.primaryActionGroup?.skills}
                    issueType={issueType}
                    setHoveredIssueType={setHoveredIssueType}
                    forceHoverIssue={hoveredIssueId?.includes(
                      solution.id || ""
                    )}
                    setIsIssueHovered={setIsIssueHovered}
                    setIssueHoveredProps={setIssueHoveredProps}
                    geoLocation={geoLocation}
                    innerRef={isLast ? inViewRef : undefined}
                    title={solution.title}
                    location={location}

                    profileImage={solution?.user?.avatar}
                    firstName={solution.user?.firstName}
                    lastName={solution.user?.lastName}
                    createdAt={solution.createdAt}
                    rating={
                      solution.impactRanking
                        ? getRating(solution.impactRanking)
                        : 0
                    }
                    primaryClassification="climate"
                    secondaryClassification="climate"
                    email={solution?.user?.email}
                    score={solution?.user.reputationScore}
                    tags={solution?.tags}
                    isSoftDeleted={!!solution.deletedAt}
                  />
                </div>
              );
            });
          })}
          {noSolutionsFound && (
            <div className="flex   justify-center col-span-6">
              <p className=" max-w-[700px] text-center text-xl">
                There are no solutions found for the chosen location, please
                adjust your search parameters or
                <span
                  onClick={handleProposeSolution}
                  className="font-bold px-1 hover:text-white cursor-pointer"
                >
                  propose a solution
                </span>
              </p>
            </div>
          )}
        </div>
        <div
          className={classnames("relative lg:rounded-lg lg:overflow-hidden ", {
            "hidden lg:flex lg:flex-col row-start-1 lg:col-start-4": !open,
            "lg:col-start-2 lg:col-end-[-1] ": open,
          })}
        >
          <ListMap
            setOpenMap={setOpen}
            hoveredIssueType={hoveredIssueType}
            setHoveredIssueId={setHoveredIssueId}
            issueCardHovered={isIssueHovered}
            issueCardProps={issueHoveredProps}
            innerRef={mapRef}
            mapHeight={height || dim.height}
            mapWidth={width || dim.width}
          />
          <div
            className="flex flex-col absolute top-7 left-5 cursor-pointer"
            onClick={() => setOpen((open) => !open)}
          >
            {!open && <SquareLeft />}
            {open && <SquareRight />}
          </div>
        </div>
      </div>

      {/* Floating Map/List Toggle - Mobile Only */}
      <button
        onClick={() => setOpen((open) => !open)}
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 py-2.5 rounded-full shadow-lg transition-all duration-300 ease-out lg:hidden active:scale-95"
        style={{
          background: 'rgba(250, 247, 242, 0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(194, 65, 12, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(194, 65, 12, 0.08)',
        }}
      >
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-200"
          style={{
            background: 'rgba(194, 65, 12, 0.1)',
          }}
        >
          {open ? (
            <ViewListIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          ) : (
            <MapIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          )}
        </span>
        <span
          className="text-sm font-medium"
          style={{
            fontFamily: "'Source Sans 3', sans-serif",
            color: '#44403C',
          }}
        >
          {open ? "View Solutions" : "View Map"}
        </span>
      </button>
      <div className="md:hidden h-24" aria-hidden />
      {openCardModal && openedCardId && (
        <SolutionCardModal id={openedCardId} setShow={setOpenCardModal} />
      )}
      {openWarningModal && <WarningModal setShow={setOpenWarningModal} />}
    </div>
  );
}

export default SolutionList;
